/**
 * SOLFIE — "Complete seu kit" na buy box da PDP.
 *
 * ANTES: a lista vinha só do Liquid (metafield → coleção do editor → coleção do
 * produto) e mostrava simplesmente o PRIMEIRO produto da coleção — quase sempre
 * um item sem relação (numa PDP de Top Pétala aparecia jaqueta ou conjunto de
 * outra linha).
 *
 * AGORA: quem recomendar é a Shopify (`/recommendations/products.json` com
 * `intent=related`, o mesmo motor do "Combina com" e do upsell do carrinho) e a
 * lista é reordenada aqui para "completar o kit" — nesta loja CADA COR É UM
 * PRODUTO separado ("Top Pétala Manga Longa Preto", "… Verde Pistache"), então
 * "parecido" é uma combinação de linha + cor + tipo de peça:
 *
 *   5. mesma linha + MESMA cor + peça de outro tipo   (top preto → legging/short preto)
 *   4. mesma linha + MESMA cor
 *   3. mesma linha + outro tipo (outra cor)
 *   2. mesma linha (outra cor)
 *   1. qualquer outra — na ordem de relevância da Shopify (o bloco nunca fica vazio)
 *   0. descartado (a própria peça / esgotada)
 *
 * "Linha" são as palavras do título que sobram depois de tirar cor e tipo
 * ("Pétala", "Retrô", "Aura", "Sunny", "Airlift Linea", "Studio 70"…), o que
 * funciona bem neste catálogo em que o título carrega a linha e a cor.
 *
 * Se o fetch falhar, se a Shopify não devolver nada ou se o JS estiver off, os
 * itens renderizados pelo Liquid continuam na tela (fallback) — o script só
 * troca a lista quando encontra candidato melhor.
 *
 * Configuração via data-attributes em `.solfie-ad` (snippets/solfie-ad-list.liquid).
 */
(function () {
  'use strict';

  var TIPOS = [
    'top', 'blusa', 'regata', 'colete', 'jaqueta', 'cropped', 'body',
    'legging', 'calca', 'short', 'saia', 'macacao', 'macaquinho', 'vestido', 'conjunto', 'pantalona'
  ];

  var IGNORAR = ['de', 'da', 'do', 'das', 'dos', 'com', 'e', 'a', 'o', 'os', 'as',
    'para', 'por', 'em', '+', 'peca', 'pecas', 'unissex'];

  /*
   * Vocabulário de cores da loja. Serve para tirar a cor do TÍTULO (é o campo
   * mais consistente: 28 dos 142 produtos não têm opção "Cor" — "Macacão Pétala
   * Marrom", "Conjunto Pétala Regata Frente Única Preto" — e as tags de cor
   * também faltam). Guarda a forma masculina e a feminina ("branco"/"branca"),
   * que é exatamente como os títulos variam.
   */
  var CORES = ['preto', 'preta', 'branco', 'branca', 'offwhite', 'off', 'white',
    'marrom', 'mocha', 'canela', 'caramelo', 'bege', 'nude', 'avela',
    'rosa', 'pink', 'rose', 'vermelho', 'vermelha', 'verde', 'musgo', 'pistache', 'olive', 'oliva',
    'azul', 'marinho', 'candy', 'amarelo', 'amarela', 'cinza', 'chumbo', 'grafite',
    'lavanda', 'purple', 'roxo', 'roxa', 'lilas', 'dourado', 'prata', 'creme', 'lima'];

  function semAcento(txt) {
    var s = String(txt == null ? '' : txt);
    try {
      s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (e) { /* navegador sem normalize: segue sem tirar acento */ }
    return s.toLowerCase();
  }

  function palavras(txt) {
    /* hífen vira espaço: "Off-white" e "Off White" precisam casar */
    return semAcento(txt).replace(/[^a-z0-9\s+]/g, ' ').split(/\s+/).filter(Boolean);
  }

  function escapar(txt) {
    return String(txt == null ? '' : txt).replace(/[&<>"']/g, function (c) {
      var mapa = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
      return mapa[c];
    });
  }

  function dinheiro(centavos, formato, locale, moeda) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && formato) {
      try { return window.Shopify.formatMoney(centavos, formato); } catch (e) { /* segue */ }
    }
    try {
      return (centavos / 100).toLocaleString(locale, { style: 'currency', currency: moeda });
    } catch (e) {
      return 'R$ ' + (centavos / 100).toFixed(2).replace('.', ',');
    }
  }

  function comLargura(url, largura) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + largura;
  }

  /* ---------- cor / tipo / linha ---------- */

  /* tokens do texto que pertencem ao vocabulário de cores */
  function corDoTexto(txt) {
    return palavras(txt).filter(function (p) { return CORES.indexOf(p) !== -1; });
  }

  function juntarUnicos(listas) {
    var saida = [];
    listas.forEach(function (l) {
      (l || []).forEach(function (p) { if (saida.indexOf(p) === -1) saida.push(p); });
    });
    return saida;
  }

  /* valor da opção "Cor"/"Color" (o nome varia: Cor, Color, Size…) */
  function opcaoCor(produto) {
    var ops = produto.options || [];
    for (var i = 0; i < ops.length; i++) {
      var nome = semAcento(ops[i].name || '');
      if (nome.indexOf('cor') === 0 || nome.indexOf('color') === 0) {
        var valores = ops[i].values || [];
        return valores.length ? String(valores[0]) : '';
      }
    }
    return '';
  }

  /*
   * Cor do produto = vocabulário aplicado ao TÍTULO + opção "Cor" + tags.
   * O título é a base (funciona nos 142 produtos); a opção reforça a forma
   * ("Branca" no título + "Branco" na opção) e as tags cobrem o resto.
   */
  function corTokensDe(produto) {
    return juntarUnicos([
      corDoTexto(produto.title),
      corDoTexto(opcaoCor(produto)),
      corDoTexto((produto.tags || []).join(' '))
    ]);
  }

  function tipoDe(titulo) {
    var ps = palavras(titulo);
    for (var i = 0; i < ps.length; i++) {
      if (TIPOS.indexOf(ps[i]) !== -1) return ps[i];
    }
    return '';
  }

  function tokensLinha(titulo, corTokens) {
    return palavras(titulo).filter(function (p) {
      return IGNORAR.indexOf(p) === -1 && TIPOS.indexOf(p) === -1 && corTokens.indexOf(p) === -1;
    });
  }

  function mesmaLinha(tokensAtual, tokensCand) {
    for (var i = 0; i < tokensAtual.length; i++) {
      if (tokensCand.indexOf(tokensAtual[i]) !== -1) return true;
    }
    return false;
  }

  function interseccao(a, b) {
    return a.some(function (p) { return b.indexOf(p) !== -1; });
  }

  function restantes(a, b) {
    return a.filter(function (p) { return b.indexOf(p) === -1; });
  }

  /*
   * Mesma cor por PALAVRA, com uma ressalva: se as DUAS cores trazem um
   * qualificador e ele é diferente, não é a mesma cor —
   *   "Verde Musgo" x "Verde Pistache" -> NÃO (musgo x pistache)
   *   "Marrom"      x "Marrom Mocha"    -> SIM (um lado não tem qualificador)
   *   "Branca"      x "Branco"          -> SIM, e "Off-white" casa com "Off White"
   *     (o hífen vira espaço em `palavras`)
   */
  function mesmaCor(atual, candidato) {
    var corCand = corTokensDe(candidato);
    if (!corCand.length || !atual.corTokens.length) return false;
    if (!interseccao(atual.corTokens, corCand)) return false;

    var soAtual = restantes(atual.corTokens, corCand);
    var soCand = restantes(corCand, atual.corTokens);
    if (soAtual.length && soCand.length && !interseccao(soAtual, soCand)) return false;
    return true;
  }

  /* ---------- níveis de "parecido" ---------- */

  function pLinha(atual, cand) {
    return mesmaLinha(atual.tokens, tokensLinha(cand.title, corTokensDe(cand)));
  }

  function pCor(atual, cand) {
    return mesmaCor(atual, cand);
  }

  function pOutroTipo(atual, cand) {
    var t = tipoDe(cand.title);
    return !!t && t !== atual.tipo;
  }

  /* a MESMA peça: mesmo TIPO e o mesmo conjunto de palavras da linha que o
     produto atual (top petala manga longa = top petala manga longa em outra cor;
     "petala" sozinho não conta como mesma peça de "petala manga longa") */
  function pMesmaPeca(atual, cand) {
    if (tipoDe(cand.title) !== atual.tipo) return false;
    var tks = tokensLinha(cand.title, corTokensDe(cand));
    if (!atual.tokens.length || tks.length !== atual.tokens.length) return false;
    return atual.tokens.every(function (t) {
      return tks.indexOf(t) !== -1;
    });
  }

  /*
   * MODOS (é o que o painel controla): cada modo é uma lista ORDENADA de
   * critérios — o peso do candidato é a posição do primeiro critério que ele
   * atende (o último vale 1) e 0 quando é descartado (a própria peça, esgotada
   * ou já no carrinho).
   */
  var MODO_PADRAO = 'combina_cor';

  var MODOS = {
    /* peça que combina na mesma cor -> mesma linha em outra cor */
    combina_cor: [
      function (a, c) { return pLinha(a, c) && pCor(a, c) && pOutroTipo(a, c); },
      function (a, c) { return pLinha(a, c) && pCor(a, c); },
      function (a, c) { return pLinha(a, c) && pOutroTipo(a, c); },
      pLinha
    ],
    /* mesma linha e mesma cor primeiro, de qualquer tipo */
    mesma_cor: [
      function (a, c) { return pLinha(a, c) && pCor(a, c); },
      function (a, c) { return pLinha(a, c) && pOutroTipo(a, c); },
      pLinha
    ],
    /* outra cor da MESMA peça primeiro (top preto -> top verde) */
    mesma_peca: [
      pMesmaPeca,
      function (a, c) { return pLinha(a, c) && pCor(a, c); },
      function (a, c) { return pLinha(a, c) && pOutroTipo(a, c); },
      pLinha
    ],
    /* qualquer peça da mesma linha */
    linha: [pLinha],
    /* sem ranking: só a ordem de relevância da Shopify */
    shopify: []
  };

  function prioridade(atual, candidato, opcoes) {
    var opt = opcoes || {};
    if (String(candidato.id) === String(atual.id)) return 0;
    if (opt.incluirEsgotados !== true && candidato.available === false) return 0;
    if (opt.excluirCarrinho !== false && candidato.noCarrinho) return 0;

    var criterios = MODOS[opt.modo] || MODOS[MODO_PADRAO];
    for (var i = 0; i < criterios.length; i++) {
      if (criterios[i](atual, candidato)) return criterios.length - i;
    }
    return 1;
  }

  /* ---------- montagem do item (mesmo markup do Liquid) ---------- */

  function montarItem(produto, cfg) {
    var variantes = produto.variants || [];
    var variante = null;
    for (var i = 0; i < variantes.length; i++) {
      if (variantes[i].available) { variante = variantes[i]; break; }
    }
    if (!variante && variantes.length) variante = variantes[0];

    var disponivel = produto.available !== false && !!variante;
    var preco = variante ? variante.price : produto.price;
    var comparar = variante ? variante.compare_at_price : produto.compare_at_price;
    var capa = comLargura(produto.featured_image || (produto.images || [])[0], 320);
    var titulo = escapar(produto.title);

    var precoHtml = disponivel
      ? '<span class="solfie-ad__price">' + escapar(dinheiro(preco, cfg.moneyFormat, cfg.locale, cfg.moeda)) + '</span>' +
        (comparar && comparar > preco
          ? '<span class="solfie-ad__compare">' + escapar(dinheiro(comparar, cfg.moneyFormat, cfg.locale, cfg.moeda)) + '</span>'
          : '')
      : '<span class="solfie-ad__sold">' + escapar(cfg.textoSold) + '</span>';

    var media = capa
      ? '<img class="solfie-ad__img" src="' + capa + '" alt="' + titulo +
        '" loading="lazy" decoding="async" width="320" height="320">'
      : '<span class="solfie-ad__ph"></span>';

    return '<label class="solfie-ad__item" data-role="item" data-price="' + (preco || 0) +
      '" data-variant-id="' + (variante ? variante.id : '') + '">' +
      '<input type="checkbox" class="solfie-ad__input" data-role="toggle" value="' +
      (variante ? variante.id : '') + '"' + (disponivel ? '' : ' disabled') + '>' +
      '<span class="solfie-ad__media">' + media + '</span>' +
      '<span class="solfie-ad__info">' +
      '<span class="solfie-ad__name">' + titulo + '</span>' +
      '<span class="solfie-ad__meta">' + precoHtml + '</span>' +
      '</span>' +
      '<span class="solfie-ad__switch" aria-hidden="true"><span class="solfie-ad__knob"></span></span>' +
      '</label>';
  }

  /* ---------- carrinho / toggle ---------- */

  var CABECALHO = { 'Content-Type': 'application/json', Accept: 'application/json' };

  var root = null;
  var cfg = null;
  var carrinho = null;
  var status = null;

  function chaveDaVariante(cart, variantId) {
    var achou = null;
    ((cart && cart.items) || []).forEach(function (i) {
      if (String(i.variant_id) === String(variantId)) achou = i.key;
    });
    return achou;
  }

  function lerCarrinho() {
    return window.fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* avisa o tema: o cart drawer e a pagina do carrinho se atualizam sozinhos */
  function avisar() {
    if (!carrinho) return;
    document.querySelectorAll('[data-cart-count], .cart-bubble__count').forEach(function (el) {
      el.textContent = carrinho.item_count;
    });
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: carrinho } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: carrinho } }));
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: carrinho,
        sourceId: 'solfie-ad',
        data: { source: 'solfie-ad', itemCount: carrinho.item_count, didError: false }
      }
    }));
  }

  function itens() {
    return Array.prototype.slice.call(root.querySelectorAll('[data-role="item"]'));
  }

  function sincronizar() {
    itens().forEach(function (it) {
      var inp = it.querySelector('[data-role="toggle"]');
      if (!inp || inp.disabled) return;
      inp.checked = !!chaveDaVariante(carrinho, it.dataset.variantId);
    });
  }

  function marcarStatus(txt, erro) {
    if (!status) return;
    status.textContent = txt || '';
    status.className = 'solfie-ad__status' + (erro ? ' is-error' : '');
  }

  function alternar(it) {
    var inp = it.querySelector('[data-role="toggle"]');
    var variante = it.dataset.variantId;
    var chave = chaveDaVariante(carrinho, variante);
    var jaTem = !!chave;

    it.classList.add('is-carregando');
    marcarStatus('', false);

    var pedido = jaTem
      ? window.fetch('/cart/change.js', {
          method: 'POST',
          headers: CABECALHO,
          body: JSON.stringify({ id: chave, quantity: 0 })
        })
      : window.fetch('/cart/add.js', {
          method: 'POST',
          headers: CABECALHO,
          body: JSON.stringify({ items: [{ id: Number(variante), quantity: 1 }] })
        });

    pedido
      .then(function (r) {
        if (!r.ok) throw new Error('falhou');
        return r.json();
      })
      .then(lerCarrinho)
      .then(function (cart) {
        carrinho = cart;
        it.classList.remove('is-carregando');
        sincronizar();
        avisar();
        marcarStatus(jaTem ? 'Item removido do carrinho.' : 'Item adicionado ao carrinho.', false);
        window.setTimeout(function () { marcarStatus('', false); }, 2600);
      })
      .catch(function () {
        it.classList.remove('is-carregando');
        if (inp) inp.checked = jaTem;
        marcarStatus('Não foi possível atualizar o carrinho. Tente de novo.', true);
      });
  }

  function ligarItens() {
    itens().forEach(function (it) {
      var inp = it.querySelector('[data-role="toggle"]');
      if (!inp || inp.disabled || inp.dataset.adBound) return;
      inp.dataset.adBound = '1';
      inp.addEventListener('change', function () { alternar(it); });
    });
  }

  /* ---------- recomendações + ranking ---------- */

  function trocarLista(lista, produtos) {
    var atual = cfg.atual;
    var idsCarrinho = cfg.carrinhoIds || {};
    produtos.forEach(function (p) { p.noCarrinho = !!idsCarrinho[String(p.id)]; });

    var ordenados = produtos
      .map(function (p, i) { return { p: p, i: i, peso: prioridade(atual, p, cfg) }; })
      .filter(function (o) { return o.peso > 0; })
      .sort(function (a, b) {
        /* 1) o nível do modo escolhido; 2) preço mais próximo do produto atual
           (quando ligado); 3) ordem de relevância da Shopify */
        if (b.peso !== a.peso) return b.peso - a.peso;
        if (cfg.desempatePreco) {
          var d = Math.abs((a.p.price || 0) - cfg.preco) - Math.abs((b.p.price || 0) - cfg.preco);
          if (d) return d;
        }
        return a.i - b.i;
      });

    if (!ordenados.length) return false;

    var escolhidos = ordenados.slice(0, cfg.limite).map(function (o) { return o.p; });
    /* nada melhor do que o fallback do Liquid? então deixa o Liquid no lugar */
    var melhorou = escolhidos.some(function (p) { return prioridade(atual, p, cfg) > 1; });
    if (!melhorou) return false;

    lista.innerHTML = escolhidos.map(function (p) { return montarItem(p, cfg); }).join('');
    /* o Liquid pode ter escondido o bloco (sem metafield/coleção de fallback):
       agora que há item, ele aparece */
    root.hidden = false;
    ligarItens();
    sincronizar();
    return true;
  }

  function buscarRecomendacoes(lista) {
    var url = '/recommendations/products.json?product_id=' + encodeURIComponent(cfg.productId) +
      '&limit=' + cfg.buscar + '&intent=' + encodeURIComponent(cfg.intent);

    window.fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (dados) {
        var produtos = (dados && dados.products) || [];
        var trocou = produtos.length ? trocarLista(lista, produtos) : false;
        /* as recomendações não tinham NADA da mesma linha (acontece em linha
           pequena, ex. "Studio 70"): tenta a busca da loja antes de desistir */
        if (!trocou && cfg.busca) buscarLinha(lista);
      })
      .catch(function () { if (cfg.busca) buscarLinha(lista); });
  }

  /* ---------- 2ª fonte: busca da loja (mesma linha em outro produto) ---------- */

  function produtoCompleto(resumo) {
    return window.fetch('/products/' + resumo.handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function buscarLinha(lista) {
    var termo = cfg.atual.tokens.join(' ');
    if (!termo) return;

    /* o payload do suggest.json não traz variantes, por isso cada candidato é
       buscado em /products/<handle>.js (só nesta 2ª fonte) */
    var url = '/search/suggest.json?q=' + encodeURIComponent(termo) +
      '&resources[type]=product&resources[limit]=' + (cfg.limite + 3);

    window.fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (dados) {
        var recursos = ((dados || {}).resources || {}).results || {};
        var resumos = (recursos.products || []).filter(function (p) {
          return String(p.id) !== String(cfg.productId);
        }).slice(0, cfg.limite + 2);
        if (!resumos.length) return null;
        return Promise.all(resumos.map(produtoCompleto));
      })
      .then(function (produtos) {
        if (!produtos) return;
        trocarLista(lista, produtos.filter(function (p) { return !!p; }));
      })
      .catch(function () { /* fica o fallback do Liquid */ });
  }

  /* ---------- init ---------- */

  function idsNoCarrinho(cart) {
    var ids = {};
    ((cart && cart.items) || []).forEach(function (i) {
      ids[String(i.product_id)] = 1;
    });
    return ids;
  }

  function iniciar(bloco) {
    if (!bloco || bloco.dataset.adInit) return;
    var lista = bloco.querySelector('[data-role="list"]');
    if (!lista || !bloco.dataset.productId) return;
    bloco.dataset.adInit = '1';

    root = bloco;
    status = bloco.querySelector('[data-role="status"]');
    cfg = {
      productId: bloco.dataset.productId,
      limite: parseInt(bloco.dataset.limit, 10) || 1,
      buscar: parseInt(bloco.dataset.recs, 10) || 10,
      intent: bloco.dataset.intent || 'related',
      modo: bloco.dataset.modo || MODO_PADRAO,
      busca: bloco.dataset.busca !== 'false',
      desempatePreco: bloco.dataset.desempatePreco !== 'false',
      excluirCarrinho: bloco.dataset.excluirCarrinho !== 'false',
      incluirEsgotados: bloco.dataset.incluirEsgotados === 'true',
      curado: bloco.dataset.curado === 'true',
      moneyFormat: bloco.dataset.moneyFormat || '',
      locale: bloco.dataset.locale || 'pt-BR',
      moeda: bloco.dataset.currency || 'BRL',
      textoSold: bloco.dataset.soldText || 'Esgotado',
      titulo: bloco.dataset.productTitle || '',
      cor: bloco.dataset.productColor || '',
      preco: parseInt(bloco.dataset.productPrice, 10) || 0,
      tipo: tipoDe(bloco.dataset.productTitle || ''),
      carrinhoIds: {}
    };
    cfg.atual = {
      id: cfg.productId,
      titulo: cfg.titulo,
      cor: cfg.cor,
      corTokens: juntarUnicos([corDoTexto(cfg.titulo), corDoTexto(cfg.cor)]),
      tipo: cfg.tipo
    };
    cfg.atual.tokens = tokensLinha(cfg.titulo, cfg.atual.corTokens);

    ligarItens();
    /* o carrinho vem primeiro: é ele que diz o que NÃO sugerir (já está lá) */
    lerCarrinho()
      .then(function (cart) {
        carrinho = cart;
        cfg.carrinhoIds = idsNoCarrinho(cart);
        sincronizar();
      })
      .catch(function () {})
      .then(function () {
        /* combo curado pelo lojista (metafield solfie.combo): a lista do Liquid
           é a resposta final, ninguém mexe */
        if (!cfg.curado) buscarRecomendacoes(lista);
      });
  }

  function iniciarTodos() {
    document.querySelectorAll('.solfie-ad[data-product-id]').forEach(iniciar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciarTodos);
  } else {
    iniciarTodos();
  }

  document.addEventListener('shopify:section:load', iniciarTodos);


})();
